import React, { Fragment, useState, useRef, useEffect, useMemo } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Menu, Transition } from '@headlessui/react';
import Logo from '../UI/Logo';
import {
  HomeIcon,
  ExclamationTriangleIcon,
  ServerIcon,
  CogIcon,
  BuildingOfficeIcon,
  UsersIcon,
  DocumentTextIcon,
  CpuChipIcon,
  ShieldExclamationIcon,
  GlobeAltIcon,
  ShieldCheckIcon,
  TicketIcon,
  ChevronDownIcon,
  MagnifyingGlassIcon,
  Bars3Icon,
  XMarkIcon,
  SignalIcon,
  PaperAirplaneIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { UserRole } from '../../services/users';
import { NavigationItem, filterNavigationByRole } from '../../utils/permissions';
import { useTranslation } from 'react-i18next';

// Definição dos itens de navegação organizados por categorias (chaves em inglês)
const navigationCategories = {
  core: {
    name: 'Main',
    items: [
      { name: 'Dashboard', href: '/dashboard', icon: HomeIcon },

      {
        name: 'Events',
        href: '/events',
        icon: ExclamationTriangleIcon,
        requiredRoles: [UserRole.ANALYST, UserRole.ADMINISTRATOR],
      },
      { name: 'Assets', href: '/assets', icon: ServerIcon },
      { name: 'VIPs e Fornecedores', href: '/registration', icon: CogIcon, requiredRoles: [UserRole.CLIENT]}
    ],
  },
  management: {
    name: 'Management',
    items: [
      {
        name: 'Rules',
        href: '/rules',
        icon: CogIcon,
        requiredRoles: [UserRole.ANALYST, UserRole.ADMINISTRATOR],
      },
      {
        name: 'Companies',
        href: '/companies',
        icon: BuildingOfficeIcon,
        requiredRoles: [UserRole.ANALYST, UserRole.ADMINISTRATOR],
      },
      {
        name: 'Users',
        href: '/users',
        icon: UsersIcon,
        requiredRoles: [UserRole.ADMINISTRATOR],
      },
    ],
  },
  security: {
    name: 'Security',
    items: [
      {
        name: 'Domain Investigation',
        href: '/domain-investigation',
        icon: GlobeAltIcon,
        requiredRoles: [UserRole.ADMINISTRATOR],
      },
      { name: 'Leaked Credentials', href: '/leaked-credentials', icon: ShieldCheckIcon },
      {
        name: 'APT Feed',
        href: '/apt-feed',
        icon: SignalIcon,
      },
      {
        name: 'Takedowns',
        href: '/takedowns',
        icon: PaperAirplaneIcon,
      },
    ],
  },
  reports: {
    name: 'Reports & Support',
    items: [
      { name: 'Reports', href: '/reports', icon: DocumentTextIcon },
      { name: 'Tickets', href: '/tickets', icon: TicketIcon },
    ],
  },
};

interface NavigationHeaderProps {
  onMobileMenuToggle?: () => void;
  isMobileMenuOpen?: boolean;
  headerActions?: React.ReactNode;
}

const NavigationHeader: React.FC<NavigationHeaderProps> = ({
  onMobileMenuToggle,
  isMobileMenuOpen = false,
  headerActions,
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const dropdownRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const searchRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Criar lista plana de todos os itens de navegação para busca
  const allNavigationItems = useMemo(() => {
    if (!user) return [];
    return Object.values(navigationCategories).flatMap(category => filterNavigationByRole(category.items, user.role));
  }, [user]);

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      // Verificar se o clique foi em um link do dropdown
      const isNavLink = (target as Element)?.closest('a[href]');

      // Verificar se o clique foi fora de todos os dropdowns
      const isOutsideAllDropdowns = Object.values(dropdownRefs.current).every(ref => !ref || !ref.contains(target));

      if (isOutsideAllDropdowns && !isNavLink) {
        setOpenDropdown(null);
      }
      if (searchRef.current && !searchRef.current.contains(target)) {
        setSearchResults([]);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Busca inteligente com categorização
  useEffect(() => {
    if (searchQuery.trim() && user) {
      const query = searchQuery.toLowerCase();
      const results = allNavigationItems
        .filter(item => item.name.toLowerCase().includes(query) || item.href.toLowerCase().includes(query))
        .map(item => {
          // Encontrar a categoria do item
          const category = Object.entries(navigationCategories).find(([_, cat]) =>
            cat.items.some(catItem => catItem.href === item.href),
          );

          return {
            ...item,
            category: category ? category[1].name : t('Others'),
            categoryIcon: HomeIcon
          };
        })
        .slice(0, 8);

      setSearchResults(results);
    } else {
      setSearchResults([]);
    }
  }, [searchQuery, allNavigationItems, user]);

  // Atalhos de teclado para busca
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Ctrl/Cmd + K para focar na busca
      if ((event.ctrlKey || event.metaKey) && event.key === 'k') {
        event.preventDefault();
        searchInputRef.current?.focus();
      }

      // Escape para limpar busca
      if (event.key === 'Escape' && isSearchFocused) {
        setSearchQuery('');
        setSearchResults([]);
        searchInputRef.current?.blur();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isSearchFocused]);

  if (!user) {
    return null;
  }

  // Mapeamento de rotas para breadcrumbs mais inteligente
  const routeMapping: Record<string, { name: string; parent?: string }> = {
    '/': { name: 'Dashboard' },
    '/dashboard': { name: 'Dashboard' },

    '/events': { name: 'Events' },
    '/assets': { name: 'Assets' },

    '/companies': { name: 'Companies' },
    '/users': { name: 'Users' },
    '/users/create': { name: 'Create User', parent: '/users' },
    '/users/edit': { name: 'Edit User', parent: '/users' },
    '/registration': { name: 'VIPs e Fornecedores' },
    '/domain-investigation': { name: 'Domain Investigation' },
    '/leaked-credentials': { name: 'Leaked Credentials' },
    '/reports': { name: 'Reports' },
    '/reports/custom': { name: 'Custom Reports', parent: '/reports' },
    '/reports/scheduled': { name: 'Scheduled Reports', parent: '/reports' },
    '/tickets': { name: 'Tickets' },
    '/tickets/create': { name: 'Create Ticket', parent: '/tickets' },
    '/tickets/edit': { name: 'Edit Ticket', parent: '/tickets' },
    '/takedowns': { name: 'Takedowns' },
  };

  // Função para gerar breadcrumbs baseado na rota atual
  const generateBreadcrumbs = () => {
    const currentRoute = routeMapping[location.pathname];
    const breadcrumbs = [];

    if (!currentRoute) {
      // Fallback para rotas não mapeadas
      const pathSegments = location.pathname.split('/').filter(Boolean);
      breadcrumbs.push({ name: 'Dashboard', href: '/dashboard', current: false });

      let currentPath = '';
      pathSegments.forEach((segment, index) => {
        currentPath += `/${segment}`;
        const isLast = index === pathSegments.length - 1;

        // Tentar encontrar na navegação primeiro
        const navItem = Object.values(navigationCategories)
          .flatMap(category => category.items)
          .find(item => item.href === currentPath);

        const name = navItem
          ? navItem.name
          : segment
              .split('-')
              .map(word => word.charAt(0).toUpperCase() + word.slice(1))
              .join(' ');

        breadcrumbs.push({
          name,
          href: currentPath,
          current: isLast,
        });
      });

      return breadcrumbs;
    }

    // Construir breadcrumbs hierárquicos
    const buildHierarchy = (route: string): Array<{ name: string; href: string; current: boolean }> => {
      const routeInfo = routeMapping[route];
      if (!routeInfo) return [];

      const hierarchy = [];

      if (routeInfo.parent) {
        hierarchy.push(...buildHierarchy(routeInfo.parent));
      } else if (route !== '/dashboard') {
        // Adicionar Dashboard como root se não houver parent específico
        hierarchy.push({ name: 'Dashboard', href: '/dashboard', current: false });
      }

      hierarchy.push({
        name: routeInfo.name,
        href: route,
        current: route === location.pathname,
      });

      return hierarchy;
    };

    return buildHierarchy(location.pathname);
  };

  const breadcrumbs = generateBreadcrumbs();

  // Função para verificar se uma categoria tem itens ativos
  const isCategoryActive = (category: any) => {
    const filteredItems = filterNavigationByRole(category.items, user.role);
    return filteredItems.some(item => location.pathname.startsWith(item.href));
  };

  // Função para busca global (placeholder)
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    // TODO: Implementar busca global
    console.log('Buscar por:', searchQuery);
  };

  const handleSearchSelect = (item: any) => {
    setSearchQuery('');
    setSearchResults([]);
    setIsSearchFocused(false);
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchResults.length > 0) {
      // Navegar para o primeiro resultado
      window.location.href = searchResults[0].href;
      handleSearchSelect(searchResults[0]);
    }
  };

  const toggleDropdown = (dropdownName: string) => {
    setOpenDropdown(openDropdown === dropdownName ? null : dropdownName);
  };

  return (
    <>
      {/* Header Principal */}
      <div className='bg-white/95 dark:bg-gray-900/95 backdrop-blur-md shadow-lg border-b border-gray-200/50 dark:border-gray-700/50 sticky top-0 z-40 transition-all duration-300'>
        {/* Primeira linha - Logo, Navegação e Ações */}
        <div className='px-4 sm:px-6 lg:px-8'>
          <div className='flex items-center justify-between h-16'>
            {/* Logo e Menu Mobile */}
            <div className='flex items-center space-x-4'>
              {/* Botão Menu Mobile */}
              <button
                type='button'
                className='lg:hidden -m-2.5 p-2.5 text-gray-700 dark:text-gray-300 transition-colors duration-200'
                onClick={onMobileMenuToggle}
              >
                <span className='sr-only'>Abrir menu</span>
                <div className='relative w-6 h-6'>
                  <span
                    className={`absolute block h-0.5 w-6 bg-current transform transition-all duration-300 ease-in-out ${
                      isMobileMenuOpen ? 'rotate-45 translate-y-0' : '-translate-y-2'
                    }`}
                  />
                  <span
                    className={`absolute block h-0.5 w-6 bg-current transform transition-all duration-300 ease-in-out ${
                      isMobileMenuOpen ? 'opacity-0' : 'opacity-100'
                    }`}
                  />
                  <span
                    className={`absolute block h-0.5 w-6 bg-current transform transition-all duration-300 ease-in-out ${
                      isMobileMenuOpen ? '-rotate-45 translate-y-0' : 'translate-y-2'
                    }`}
                  />
                </div>
              </button>

              {/* Logo */}
              <div className='flex items-center space-x-3'>
                <Logo className='h-8 w-auto' alt='Mermaid Logo' />
                <span className='text-xl font-bold text-gradient dark:text-white hidden sm:block'>Mermaid</span>
              </div>
            </div>

            {/* Navegação Principal - Desktop */}
            <nav className='hidden lg:flex lg:space-x-1'>
              {Object.entries(navigationCategories).map(([key, category]) => {
                const filteredItems = filterNavigationByRole(category.items, user.role);
                if (filteredItems.length === 0) return null;
                const isOpen = openDropdown === key;
                const isActive = isCategoryActive(category);

                return (
                  <div
                    key={key}
                    className='relative'
                    ref={el => {
                      dropdownRefs.current[key] = el;
                    }}
                  >
                    <button
                      onClick={() => toggleDropdown(key)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center space-x-1 ${
                        isActive
                          ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-400'
                          : isOpen
                            ? 'bg-gray-50 text-gray-900 dark:bg-gray-700 dark:text-gray-100'
                            : 'text-gray-700 hover:text-primary-600 hover:bg-gray-50 dark:text-gray-300 dark:hover:text-primary-400 dark:hover:bg-gray-800'
                      }`}
                    >
                      <span>{t(category.name)}</span>
                      <ChevronDownIcon
                        className={`h-4 w-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                      />
                    </button>

                    {/* Dropdown Menu */}
                    <div className={`absolute left-0 z-50 mt-2 w-64 origin-top-left rounded-lg bg-white dark:bg-gray-800 shadow-xl ring-1 ring-black ring-opacity-5 border border-gray-200 dark:border-gray-700 transition-all duration-200 ${
                      isOpen
                        ? 'opacity-100 scale-100 translate-y-0' 
                        : 'opacity-0 scale-95 -translate-y-2 pointer-events-none'
                    }`}
                    >
                      <div className='py-2'>
                        <div className='px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider border-b border-gray-100 dark:border-gray-700'>
                          {t(category.name)}
                        </div>
                        {filteredItems.map(item => {
                          const isItemActive = location.pathname === item.href;
                          return (
                            <NavLink
                              key={item.href}
                              to={item.href}
                              onClick={() => setOpenDropdown(null)}
                              className={`group flex items-center px-4 py-3 text-sm transition-all duration-200 ${
                                isItemActive
                                  ? 'text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20 border-r-2 border-primary-600 dark:border-primary-400'
                                  : 'text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-700'
                              }`}
                            >
                              <item.icon
                                className={`mr-3 h-4 w-4 transition-colors duration-200 ${
                                  isItemActive
                                    ? 'text-primary-600 dark:text-primary-400'
                                    : 'text-gray-400 group-hover:text-gray-500 dark:group-hover:text-gray-300'
                                }`}
                                aria-hidden='true'
                              />
                              <div className='flex-1'>
                                <div className='font-medium'>{t(item.name)}</div>
                              </div>
                              {isItemActive && (
                                <div className='w-2 h-2 bg-primary-600 dark:bg-primary-400 rounded-full'></div>
                              )}
                            </NavLink>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                );
              })}
            </nav>

            {/* Busca Mobile */}
            <div className='flex-1 mx-4 md:hidden'>
              <div className='relative'>
                <div className='absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none'>
                  <MagnifyingGlassIcon className='h-4 w-4 text-gray-400' aria-hidden='true' />
                </div>
                <input
                  type='text'
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className='block w-full pl-9 pr-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-transparent'
                  placeholder={t('Search...')}
                />
              </div>
            </div>

            {/* Busca Global */}
            <div className='flex-1 max-w-md mx-8 hidden md:block' ref={searchRef}>
              <form onSubmit={handleSearchSubmit} className='relative'>
                <div className='absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none'>
                  <MagnifyingGlassIcon className='h-5 w-5 text-gray-400' aria-hidden='true' />
                </div>
                <input
                  ref={searchInputRef}
                  type='text'
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  onFocus={() => setIsSearchFocused(true)}
                  onBlur={() => setTimeout(() => setIsSearchFocused(false), 200)}
                  className='block w-full pl-10 pr-20 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200'
                  placeholder={t('Search pages, features...')}
                />

                {/* Atalho de teclado */}
                <div className='absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none'>
                  <kbd className='hidden sm:inline-flex items-center px-2 py-1 text-xs font-medium text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-600 rounded border border-gray-300 dark:border-gray-500'>
                    ⌘K
                  </kbd>
                </div>

                {/* Resultados da busca */}
                {searchResults.length > 0 && (
                  <div className='absolute top-full left-0 right-0 mt-2 bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 z-50 overflow-hidden'>
                    <div className='py-2'>
                      <div className='px-4 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider border-b border-gray-100 dark:border-gray-700'>
                          {t('Search results')} ({searchResults.length})
                      </div>
                      {searchResults.map((item, index) => (
                        <NavLink
                          key={item.href}
                          to={item.href}
                          onClick={() => handleSearchSelect(item)}
                          className='group flex items-center px-4 py-3 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200 border-l-2 border-transparent hover:border-primary-500'
                        >
                          <div className='flex items-center justify-center w-8 h-8 rounded-lg bg-gray-100 dark:bg-gray-700 group-hover:bg-primary-100 dark:group-hover:bg-primary-900/20 transition-colors duration-200'>
                            <item.icon
                              className='h-4 w-4 text-gray-500 dark:text-gray-400 group-hover:text-primary-600 dark:group-hover:text-primary-400'
                              aria-hidden='true'
                            />
                          </div>
                          <div className='ml-3 flex-1'>
                            <div className='font-medium text-gray-900 dark:text-gray-100 group-hover:text-primary-600 dark:group-hover:text-primary-400'>
                                {t(item.name)}
                            </div>
                            <div className='text-xs text-gray-500 dark:text-gray-400 flex items-center mt-0.5'>
                              <item.categoryIcon className='h-3 w-3 mr-1' />
                                {t(item.category)}
                            </div>
                          </div>
                          {index === 0 && (
                            <div className='text-xs text-gray-400 dark:text-gray-500'>
                              <kbd className='px-1.5 py-0.5 text-xs font-medium bg-gray-100 dark:bg-gray-600 rounded border'>
                                ↵
                              </kbd>
                            </div>
                          )}
                        </NavLink>
                      ))}

                      {searchQuery.trim() && searchResults.length === 0 && (
                        <div className='px-4 py-6 text-center text-gray-500 dark:text-gray-400'>
                          <MagnifyingGlassIcon className='h-8 w-8 mx-auto mb-2 opacity-50' />
                          <p className='text-sm'>{t('No results found')}</p>
                          <p className='text-xs mt-1'>{t('Try different terms')}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </form>
            </div>

            {/* Ações do header */}
            <div className='flex items-center space-x-2'>
              {headerActions && <div className='flex items-center space-x-2'>{headerActions}</div>}
            </div>
          </div>
        </div>

        {/* Segunda linha - Breadcrumbs */}
        <div className='hidden md:block px-4 sm:px-6 lg:px-8 py-2 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-700'>
          <nav className='flex items-center space-x-1 text-sm overflow-x-auto' aria-label='Breadcrumb'>
            <ol className='flex items-center space-x-1 whitespace-nowrap'>
              {breadcrumbs.map((crumb, index) => (
                <li key={crumb.href} className='flex items-center'>
                  {index > 0 && (
                    <svg
                      className='h-4 w-4 text-gray-400 dark:text-gray-500 mx-2 flex-shrink-0'
                      fill='currentColor'
                      viewBox='0 0 20 20'
                      aria-hidden='true'
                    >
                      <path
                        fillRule='evenodd'
                        d='M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z'
                        clipRule='evenodd'
                      />
                    </svg>
                  )}
                  {crumb.current ? (
                    <span
                      className='text-gray-900 dark:text-gray-100 font-medium px-2 py-1 rounded-md bg-gray-100 dark:bg-gray-800 flex-shrink-0'
                      aria-current='page'
                    >
                      {t(crumb.name)}
                    </span>
                  ) : (
                    <NavLink
                      to={crumb.href}
                      className='text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors duration-200 px-2 py-1 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800 flex-shrink-0'
                    >
                      {t(crumb.name)}
                    </NavLink>
                  )}
                </li>
              ))}
            </ol>
          </nav>
        </div>

        {/* Breadcrumb Mobile - Simplificado */}
        <div className='md:hidden px-4 py-2 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-700'>
          <div className='flex items-center justify-between'>
            <div className='flex items-center space-x-2 text-sm'>
              {breadcrumbs.length > 1 && (
                <NavLink
                  to={breadcrumbs[breadcrumbs.length - 2].href}
                  className='text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors duration-200'
                >
                  <svg className='h-4 w-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M15 19l-7-7 7-7' />
                  </svg>
                </NavLink>
              )}
              <span className='text-gray-900 dark:text-gray-100 font-medium truncate'>
                {t(breadcrumbs[breadcrumbs.length - 1]?.name || '')}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Menu Mobile */}
      <div
        className={`lg:hidden fixed inset-0 z-50 transition-opacity duration-300 ${
          isMobileMenuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
      >
        <div className='fixed inset-0 bg-gray-600 bg-opacity-75' onClick={onMobileMenuToggle} />
        <div
          className={`fixed inset-y-0 left-0 w-full max-w-sm bg-white dark:bg-gray-900 shadow-xl transform transition-transform duration-300 ease-in-out ${
            isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          <div className='flex items-center justify-between h-16 px-4 border-b border-gray-200 dark:border-gray-700'>
            <div className='flex items-center space-x-3 group'>
              <div className='h-8 w-8 bg-gradient-to-br from-primary-500 via-primary-600 to-secondary-600 rounded-xl flex items-center justify-center shadow-lg group-hover:shadow-xl group-hover:scale-105 transition-all duration-300'>
                <span className='text-white font-bold text-lg drop-shadow-sm'>M</span>
              </div>
              <span className='text-xl font-bold bg-gradient-to-r from-primary-600 via-primary-700 to-secondary-600 bg-clip-text text-transparent dark:from-primary-400 dark:via-primary-300 dark:to-secondary-400 group-hover:from-primary-500 group-hover:to-secondary-500 transition-all duration-300'>
                Mermaid
              </span>
            </div>
            <button
              type='button'
              className='-m-2.5 p-2.5 text-gray-700 dark:text-gray-300 transition-colors duration-200'
              onClick={onMobileMenuToggle}
            >
              <XMarkIcon className='h-6 w-6' aria-hidden='true' />
            </button>
          </div>

          {/* Busca Mobile no Menu */}
          <div className='px-4 py-3 border-b border-gray-200 dark:border-gray-700'>
            <div className='relative'>
              <div className='absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none'>
                <MagnifyingGlassIcon className='h-5 w-5 text-gray-400' aria-hidden='true' />
              </div>
              <input
                type='text'
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className='block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent'
                placeholder={t('Search pages, features...')}
              />
            </div>
          </div>

          <nav className='px-4 py-6 space-y-6 overflow-y-auto flex-1'>
            {Object.entries(navigationCategories).map(([key, category]) => {
              const filteredItems = filterNavigationByRole(category.items, user.role);

              if (filteredItems.length === 0) return null;

              return (
                <div key={key}>
                  <h3 className='text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3'>
                      {t(category.name)}
                  </h3>
                  <div className='space-y-1'>
                    {filteredItems.map(item => (
                      <NavLink
                        key={item.name}
                        to={item.href}
                        onClick={onMobileMenuToggle}
                        className={({ isActive }) =>
                          `flex items-center px-3 py-3 rounded-lg text-sm font-medium transition-all duration-200 border-l-2 ${
                            isActive
                              ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-400 border-primary-600 dark:border-primary-400'
                              : 'text-gray-700 hover:text-primary-600 hover:bg-gray-50 dark:text-gray-300 dark:hover:text-primary-400 dark:hover:bg-gray-800 border-transparent hover:border-primary-300'
                          }`
                        }
                      >
                        <item.icon className='mr-3 h-5 w-5' aria-hidden='true' />
                        <span className='flex-1'>{t(item.name)}</span>
                        {location.pathname === item.href && (
                          <div className='w-2 h-2 bg-primary-600 dark:bg-primary-400 rounded-full'></div>
                        )}
                      </NavLink>
                    ))}
                  </div>
                </div>
              );
            })}
          </nav>
        </div>
      </div>
    </>
  );
};

export default NavigationHeader;
